import './style.scss';

// Our Services: a one-shot intro, then a pinned, scroll-scrubbed card stack.
//
// Timed and measured from a native 30fps every-frame extraction of the
// reference (t=153.95-155.72 for the intro and t=158.30-160.20 for the first
// handover, no frames skipped).
//
// Intro (one-shot, plays as the section arrives - the frames show a smooth
// decelerating settle independent of scroll speed, so it is a tween, not a
// scrub):
//   - card:  enters from below the fold and rises to centre over ~1.33s,
//            26px/frame at its quickest then a long tail. Its width never
//            changes on the way in - there is no scale on entry.
//   - rules: grow from each viewport edge inward, left 0..28 -> 0..249 and
//            right 897..929 -> 682..929, both finishing flush with the card's
//            edges, in step with the card's rise.
//   - title: slides DOWN into place, ~40px, settling with the rest.
//
// Handover (scrubbed against the pin - the pauses in the video are just the
// operator's scroll stopping, so only the proportions are taken from it):
//   - outgoing card: top edge drifts down 30px on a 269px-tall card (~11% of
//     its height) while it fades. Measured luminance goes 0 -> 126 against a
//     ~250 stage, i.e. it is at roughly half opacity by the time the incoming
//     card covers it. Its width is unchanged throughout - see PREV_SCALE.
//   - incoming card: rises from below the stage, again at constant width.
const INTRO_START = 'top 75%';
const INTRO_CARD_DURATION = 1.33;
const INTRO_CARD_EASE = 'power4.out';
const INTRO_TITLE_SHIFT = 40;
const INTRO_TITLE_DURATION = 1.0;
const INTRO_LINE_DURATION = 1.25;

// A card leaving the front of the stack does not disappear. It slides to a
// "previous" position just below the active card and tints toward grey, and
// stays there, opaque, until the card AFTER the next one starts arriving -
// only then does it fade for real. So at most two cards are ever visible: the
// active one and the one behind it.
//
// The tint replaces an opacity fade deliberately. Grey is what black at ~50%
// over the white stage looks like anyway (the reference's leaving card
// measures L=126 against a 250 stage), but an opaque card keeps the rules
// hidden behind it where a translucent one lets them show straight through.
//
// Percentages, not pixels: a function-based px value gets resolved and cached
// by GSAP on the tween's first render, which here happened before the card
// had any layout - it cached 0, start equalled end, and GSAP skipped the
// property outright.
const PREV_DRIFT = 11;              // % of the card's own height
const PREV_SCALE = 0.96;
const PREV_TINT = '#808080';

// The leaving card starts moving before the next one appears: in the
// reference it is already drifting at t=158.867 while the incoming card only
// shows at t=159.000, about 15% of a handover later.
const ARRIVE_DELAY = 0.18;          // fraction of a handover
const LEAVE_PORTION = 0.55;         // reaching the prev position finishes early
const CLEAR_PORTION = 0.45;         // the older card clears out over this

const ENTER_PERCENT = 150;

// Scroll length of one handover, and of the beat that holds a card readable
// before the next one starts, both as a fraction of the viewport height.
const HANDOVER_SCROLL = 0.9;
const HOLD_SCROLL = 0.45;

function initOurServices(block) {
	const {gsap, ScrollTrigger} = window.rebredgVendors || {};

	if (!gsap || !ScrollTrigger) {
		return;
	}

	if (block.dataset.ourServicesInitialized === 'true') {
		return;
	}

	block.dataset.ourServicesInitialized = 'true';

	if (block.closest('.editor-styles-wrapper')) {
		return;
	}

	const pin = block.querySelector('[data-services-pin]');
	const stage = block.querySelector('[data-services-stage]');
	const title = block.querySelector('[data-services-title]');
	const lines = Array.from(block.querySelectorAll('[data-services-line]'));
	const cards = Array.from(block.querySelectorAll('[data-service-card]'));
	// A card arriving is animated on its slot, a card leaving on the card
	// itself. Keeping the two on different nodes is what stops the one-shot
	// intro and the scrubbed stack from writing the same properties on the
	// same element - when they did, GSAP kept only one of them and the
	// outgoing card's drift and fade silently never rendered.
	const slots = Array.from(block.querySelectorAll('[data-service-slot]'));

	if (!pin || !stage || !cards.length || slots.length !== cards.length) {
		return;
	}

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return;
	}

	gsap.registerPlugin(ScrollTrigger);

	gsap.set(slots, {yPercent: ENTER_PERCENT, opacity: 0});
	gsap.set(cards, {yPercent: 0, scale: 1, opacity: 1, backgroundColor: '#000000'});
	slots.forEach((slot, index) => gsap.set(slot, {zIndex: 20 + index}));

	if (title) {
		gsap.set(title, {y: -INTRO_TITLE_SHIFT, autoAlpha: 0});
	}

	if (lines.length) {
		gsap.set(lines, {scaleX: 0});
	}

	// --- intro -------------------------------------------------------------
	// Killed the moment it finishes. A completed timeline still re-renders its
	// end state on every tick, so left alive this one goes on stamping y: 0 and
	// autoAlpha: 1 onto card 0 forever - which silently beat the scrubbed
	// handover below and left the outgoing card refusing to drift or fade
	// (only its scale, which the intro does not touch, got through).
	let intro;

	intro = gsap.timeline({
		scrollTrigger: {
			trigger: block,
			start: INTRO_START,
			once: true,
			invalidateOnRefresh: true,
		},
		onComplete: () => intro.kill(),
	});

	if (title) {
		intro.to(title, {
			y: 0,
			autoAlpha: 1,
			duration: INTRO_TITLE_DURATION,
			ease: 'power3.out',
		}, 0);
	}

	if (lines.length) {
		intro.to(lines, {
			scaleX: 1,
			duration: INTRO_LINE_DURATION,
			ease: 'power4.out',
		}, 0);
	}

	intro.to(slots[0], {opacity: 1, duration: 0.2, ease: 'none'}, 0);
	intro.to(slots[0], {
		yPercent: 0,
		duration: INTRO_CARD_DURATION,
		ease: INTRO_CARD_EASE,
	}, 0);

	// --- pinned handovers ---------------------------------------------------
	if (cards.length > 1) {
		const segments = cards.length - 1;

		const stack = gsap.timeline({
			scrollTrigger: {
				// The pin element, not the section: the section carries a
				// padding-top that clears the absolutely positioned site
				// header, and triggering off it would start the pin while
				// that padding was still on screen - freezing the stack
				// header-height down the viewport for the whole scrub.
				trigger: pin,
				start: 'top top',
				end: () => {
					const viewport = Math.max(window.innerHeight, 620);
					return `+=${Math.round(segments * viewport * (HANDOVER_SCROLL + HOLD_SCROLL))}`;
				},
				pin,
				pinSpacing: true,
				scrub: 0.6,
				anticipatePin: 1,
				invalidateOnRefresh: true,
			},
		});

		for (let index = 1; index < cards.length; index += 1) {
			const leaving = cards[index - 1];      // moves to the prev position
			const clearing = cards[index - 2];     // has been sitting there a beat
			const arriving = slots[index];
			const label = `card-${index}`;

			stack.addLabel(label);

			// fromTo with immediateRender off throughout: as the first tween of
			// its timeline this would otherwise latch card 0's pre-intro state as
			// its start and yank the card back there when the pin engaged.
			//
			// Leaving: drops to the prev position and tints. Opaque the whole
			// way, and it starts before the arriving card shows up.
			stack.fromTo(leaving, {
				yPercent: 0,
				scale: 1,
				backgroundColor: '#000000',
			}, {
				yPercent: PREV_DRIFT,
				scale: PREV_SCALE,
				backgroundColor: PREV_TINT,
				duration: HANDOVER_SCROLL * LEAVE_PORTION,
				ease: 'power2.out',
				immediateRender: false,
			}, label);

			// The leaving card's own copy goes with it. It drifts DOWN by
			// PREV_DRIFT, so that much of it is left sticking out from under
			// the card arriving over the top - and what showed there was the
			// tail of the previous card's list, legible against the tint
			// (client: "the prev card text should not be seen"). Only the
			// copy fades; the slab itself stays opaque, which is what keeps
			// the rules from showing through it.
			//
			// power2.in so the fade happens late in the window: the card is
			// still the one being read at the start of its own leave.
			const leavingContent = Array.from(leaving.children);

			if (leavingContent.length) {
				stack.fromTo(leavingContent, {
					opacity: 1,
				}, {
					opacity: 0,
					duration: HANDOVER_SCROLL * LEAVE_PORTION,
					ease: 'power2.in',
					immediateRender: false,
				}, label);
			}

			// Clearing: the card that has been in the prev position since the
			// last handover now finally goes. The only place opacity is used.
			if (clearing) {
				stack.fromTo(clearing, {
					opacity: 1,
				}, {
					opacity: 0,
					duration: HANDOVER_SCROLL * CLEAR_PORTION,
					ease: 'power1.in',
					immediateRender: false,
				}, label);
			}

			// Arriving: rises from below at constant size, on the slot.
			stack.fromTo(arriving, {
				yPercent: ENTER_PERCENT,
				opacity: 0,
			}, {
				opacity: 1,
				duration: HANDOVER_SCROLL * 0.2,
				ease: 'none',
				immediateRender: false,
			}, `${label}+=${HANDOVER_SCROLL * ARRIVE_DELAY}`);
			stack.to(arriving, {
				yPercent: 0,
				duration: HANDOVER_SCROLL,
				ease: 'power2.out',
				immediateRender: false,
			}, `${label}+=${HANDOVER_SCROLL * ARRIVE_DELAY}`);

			// Beat where the newly arrived card is simply readable.
			stack.to({}, {duration: HOLD_SCROLL});
		}
	}

	ScrollTrigger.refresh();
}

export function our_services(root = document) {
	const selector = '[data-component="our-services"]';
	const context = root || document;
	const blocks = [];

	if (context.nodeType === 1 && context.matches(selector)) {
		blocks.push(context);
	}

	if (context.querySelectorAll) {
		blocks.push(...context.querySelectorAll(selector));
	}

	blocks.forEach(initOurServices);
}
